import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Box, Chip, Theme } from "@mui/material";
import { BusinessRuleConditionAdd } from "./BusinessRuleConditionAdd";
import { makeStyles } from "tss-react/mui";
import { GridRowModel } from "@mui/x-data-grid-pro";

import { EditFormDetails } from "../../interfaces";
import { KpiTypeFieldParams } from "./BusinessRuleConditionField";
import { OPERATORS } from "../../../constants";
import { businessRuleI18n } from "../../conf/SETUP_BUSINESS_RULES";
import { KpiCatalog, useKpiCatalog } from "../../../services/Kpi";
import { Kpi, KpiType } from "../../../Alert/Filter/FilterType";
import { ActionTooltip } from "../../../components/Tooltips";

const useStyles = makeStyles()((theme: Theme) => ({
  greyText: {
    color: theme.vars.palette.text.secondary,
    fontSize: "small",
    fontWeight: "bold",
  },
}));

interface ValueFormatted {
  kpi: {
    id: number;
  };
  operator: string;
  value: number;
}

interface Value {
  id: number;
  kpi: {
    id: number;
    kpi_type: {
      id: number;
      label: string;
      rank: number;
    };
    label: string;
  };
  operator: string;
  value: number;
}

/**
 * Structure object for helper value (data sent to API)
 */
const setValueFormater = (
  conditions: KpiTypeFieldParams[]
): ValueFormatted[] => {
  const acc: ValueFormatted[] = [];

  for (const condition of conditions) {
    if (!condition.kpi?.id || !condition.kpiOperator || !condition.kpiValue) {
      continue;
    }

    acc.push({
      kpi: {
        id: condition.kpi.id,
      },
      operator: condition.kpiOperator,
      value: condition.kpiValue,
    });
  }

  return acc;
};

/**
 * Format value property to KpiTypeFieldParams.
 */
const formatValuesToKpiTypeFieldParams = (
  value: Value[],
  catalog: KpiCatalog
): KpiTypeFieldParams[] =>
  value.flatMap((val: Value) => {
    const kpiType =
      catalog.types.find(
        (kpiType: KpiType) => kpiType.id === val.kpi.kpi_type.id
      ) || null;
    const kpi = kpiType?.children?.find((kpi: Kpi) => kpi.id === val.kpi.id);

    if (!kpiType || !kpi?.format) {
      return [];
    }

    return [
      {
        kpiType,
        kpi: {
          id: val.kpi.id,
          name: kpi.name,
          label: val.kpi.label,
          order: val.kpi.kpi_type.rank,
          format: kpi.format,
        },
        kpiValue: val.value,
        kpiOperator: val.operator,
      },
    ];
  });

/**
 * BusinessRuleParameter form component.
 */
export const BusinessRuleCondition = (params: {
  filter: EditFormDetails;
  name: string;
  value: Value[] | null;
  helpers: any;
  row?: GridRowModel;
}) => {
  const { name, helpers, value, row } = params;

  const { classes } = useStyles();
  const intl = useIntl();
  const [conditions, setConditions] = useState<KpiTypeFieldParams[]>([]);
  const { data: catalog } = useKpiCatalog();

  /**
   * Removes condition.
   */
  const onDeleteListItem = useCallback(
    (id: number): void => {
      const conditionsFiltered = conditions.filter((_, index) => index !== id);

      setConditions(conditionsFiltered);
      helpers.setValue(setValueFormater(conditionsFiltered));
    },
    [conditions, helpers]
  );

  /**
   * Add a new condition to the list.
   * Check if there is a duplicate condition id and remove previous one.
   */
  const onChangeConditionAdd = useCallback(
    (params: KpiTypeFieldParams): void => {
      const newConditions = conditions.filter(
        (condition) =>
          condition.kpi?.id !== params.kpi?.id &&
          condition.kpiType?.id !== params.kpiType?.id
      );

      const conditionsToSet = [...newConditions, params];
      setConditions(conditionsToSet);
      helpers.setValue(setValueFormater(conditionsToSet));
    },
    [conditions, helpers]
  );

  /**
   * Get operator label.
   * @param operator
   */
  const getOperatorLabel = (operator: string | null): string | undefined =>
    OPERATORS.find((op) => op.name === operator)?.label;

  /**
   * Returns condition title
   */
  const chipTitle = (condition: KpiTypeFieldParams): string =>
    `${condition.kpiType?.label} : ${condition.kpi?.label} ${getOperatorLabel(
      condition.kpiOperator
    )} ${condition.kpiValue}`;

  /**
   * Returns input title
   */
  const inputTitle = (name: string): string => {
    if (businessRuleI18n[name]) {
      return intl.formatMessage({ id: businessRuleI18n[name] });
    }

    return "";
  };

  useEffect(() => {
    if (!value) {
      helpers.setValue([]);
    }
  }, [helpers, value]);

  // Format Kpi data get from row table to display tag
  useEffect(() => {
    if (row && name && row[name] && catalog) {
      setConditions(formatValuesToKpiTypeFieldParams(row[name], catalog));
    }
  }, [name, row, catalog]);

  return (
    <>
      <span className={classes.greyText}>{name && inputTitle(name)}</span>
      <Box>
        <>
          {conditions.map((condition, index) => (
            <ActionTooltip
              title={chipTitle(condition)}
              key={condition.kpi?.id}
              rawChildren
              translateTitle={false}
            >
              <Chip
                label={chipTitle(condition)}
                onDelete={() => {
                  if (condition.kpi && "id" in condition.kpi) {
                    onDeleteListItem(index);
                  }
                }}
                sx={{ marginRight: 1, marginTop: 1 }}
              />
            </ActionTooltip>
          ))}
          <BusinessRuleConditionAdd onSubmit={onChangeConditionAdd} />
        </>
      </Box>
    </>
  );
};
